import React, { useEffect } from 'react';
import { ScopedCssBaseline, ThemeProvider } from '@mui/material';

import { CoreBox, CoreTypography } from '@youscience/core';
import { brightpathTheme } from '@youscience/theme';

import useCommonAppAdminStore from '@store/CommonAppAdminStore';
import useRecommenderStore from '@store/RecommenderStore';

import { BREADCRUMBS_PAGE_TEXT } from '@constants/breadcrumbs';
import { ROUTES } from '@constants/ROUTES';

import { useUserProfile } from '@hooks/useUserProfile';
import { useSaveProfile } from './useSchoolProfile';

import { Breadcrumbs } from '@components/Breadcrumbs';
import SaveChangesModal from '@components/SaveChangesModal';

import SchoolDetailsCounselor from './components/SchoolDetailsCounselor';
import SchoolProfile from './components/SchoolProfile';
import ClassRank from './components/ClassRank';
import GPA from './components/GPA';
import Transcripts from './components/Transcripts';
import ProfileButtons from '../ProfileButtons';

import { SchoolProfileSection, schoolProfileSections, SchoolDetailsProps } from './types';
import { StyledButtonContainer, StyledHeading, StyledHeadline, StyledHero } from './RecommenderSchoolProfile.styles';

const counselorComponents: { [key in SchoolProfileSection]: React.ComponentType<SchoolDetailsProps<unknown>> } = {
  [SchoolProfileSection.SchoolDetailsCounselor]: SchoolDetailsCounselor,
  [SchoolProfileSection.SchoolProfile]: SchoolProfile,
  [SchoolProfileSection.ClassRank]: ClassRank,
  [SchoolProfileSection.GPA]: GPA,
  [SchoolProfileSection.Transcripts]: Transcripts,
};

export const RecommenderSchoolProfile = () => {
  const { getRoles } = useCommonAppAdminStore((store) => store);
  const { getRecommenderProfile, recommenderProfile } = useRecommenderStore((store) => store);

  useEffect(() => {
    const fetchData = async () => {
      await getRoles();
      await getRecommenderProfile();
    };

    void fetchData();
  }, []);

  const { firstName, lastName } = useUserProfile();
  const { sectionState, toggleExpandedSelection, saveAndContinue, saveProfile, allFormsAreValid, hasChanges } =
    useSaveProfile();

  return (
    <ThemeProvider theme={brightpathTheme}>
      <ScopedCssBaseline />

      <StyledHero>
        <Breadcrumbs
          links={[
            { to: `${ROUTES.RECOMMENDER_SPACE}${location.hash}`, text: BREADCRUMBS_PAGE_TEXT.RECOMMENDER_WORKSPACE },
          ]}
          currentPage={BREADCRUMBS_PAGE_TEXT.RECOMMENDER_PROFILE}
        />

        <CoreTypography variant="overline">
          {recommenderProfile?.highSchool
            ? `${recommenderProfile.highSchool.schoolName}, CEEB CODE: ${recommenderProfile.highSchool.schoolCode}`
            : null}
        </CoreTypography>

        <StyledHeadline>
          <CoreTypography sx={{ fontSize: '36px', marginBottom: '0.5rem' }} variant="h3">
            {firstName} {lastName}
          </CoreTypography>
        </StyledHeadline>
      </StyledHero>

      <StyledHeading>
        <CoreBox sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '1rem' }}>
          <CoreBox sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <CoreTypography variant="h4">School profile</CoreTypography>

            <CoreTypography>
              We send your profile information with all your forms. Some forms have questions that you may answer in
              bulk using the "update" button.
            </CoreTypography>
          </CoreBox>

          <ProfileButtons onSave={saveProfile} isValid={allFormsAreValid} hasChanges={hasChanges} />
        </CoreBox>

        {schoolProfileSections.map((section) => {
          const Component = counselorComponents[section];

          return (
            <Component
              key={section}
              id={section}
              isExtended={sectionState.expandedSection === section}
              isSaved={sectionState.savedSections[section]}
              onExpand={() => toggleExpandedSelection(section)}
              onSaveAndContinue={() => saveAndContinue(section)}
            />
          );
        })}

        <StyledButtonContainer>
          <ProfileButtons onSave={saveProfile} isValid={allFormsAreValid} hasChanges={hasChanges} />
        </StyledButtonContainer>
      </StyledHeading>

      <SaveChangesModal hasUnsavedChanges={hasChanges} onSaveChanges={saveProfile} />
    </ThemeProvider>
  );
};
